import { useState } from 'react';
import { CheckCircle2, Shield, CreditCard, Calendar } from 'lucide-react';
import type { AccessPolicy } from '@/types';
import { BankIdVerification } from '@/components/verification/BankIdVerification';
import { EmailVerification } from '@/components/verification/EmailVerification';
import { SmsVerification } from '@/components/verification/SmsVerification';
import { StripePayment } from '@/components/payment/StripePayment';
import { SwishPayment } from '@/components/payment/SwishPayment';

interface AccessGateFlowProps {
  deviceId: string;
  policy: AccessPolicy;
  onComplete: (tokens: GateTokens) => void;
  onCancel: () => void;
}

export interface GateTokens {
  verification_token?: string;
  payment_id?: string;
  booking_id?: string;
}

type Step = 'verification' | 'booking' | 'payment' | 'ready';

export function AccessGateFlow({ deviceId, policy, onComplete, onCancel }: AccessGateFlowProps) {
  const [tokens, setTokens] = useState<GateTokens>({});
  const [currentStep, setCurrentStep] = useState<Step>(getFirstStep(policy));
  const [paymentMethod, setPaymentMethod] = useState<'stripe' | 'swish' | null>(null);

  const steps = getRequiredSteps(policy);
  const completedSteps = getCompletedSteps(tokens, policy);

  const handleVerified = (token: string) => {
    const newTokens = { ...tokens, verification_token: token };
    setTokens(newTokens);
    advanceToNextStep('verification', newTokens, policy);
  };

  const handlePaid = (paymentId: string) => {
    const newTokens = { ...tokens, payment_id: paymentId };
    setTokens(newTokens);
    advanceToNextStep('payment', newTokens, policy);
  };

  const advanceToNextStep = (completed: Step, currentTokens: GateTokens, pol: AccessPolicy) => {
    const nextSteps = getRequiredSteps(pol).filter(
      (s) => s !== completed && !isStepComplete(s, currentTokens, pol)
    );
    if (nextSteps.length === 0) {
      onComplete(currentTokens);
    } else {
      setCurrentStep(nextSteps[0]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Step indicators */}
      <div className="flex gap-2">
        {steps.map((step) => {
          const isComplete = completedSteps.includes(step);
          const isCurrent = step === currentStep;
          return (
            <div
              key={step}
              className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                isComplete
                  ? 'bg-green-100 text-green-700'
                  : isCurrent
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-500'
              }`}
            >
              {isComplete ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : step === 'verification' ? (
                <Shield className="h-3 w-3" />
              ) : step === 'payment' ? (
                <CreditCard className="h-3 w-3" />
              ) : (
                <Calendar className="h-3 w-3" />
              )}
              {getStepLabel(step)}
            </div>
          );
        })}
      </div>

      {/* Current step content */}
      {currentStep === 'verification' && (
        <VerificationStep
          deviceId={deviceId}
          method={policy.verification_required}
          onVerified={handleVerified}
          onCancel={onCancel}
        />
      )}

      {currentStep === 'booking' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Bokning krävs</h3>
          <p className="text-sm text-muted-foreground">
            Du måste ha en aktiv bokning för att generera en nyckel till denna enhet.
            Gå till bokningssidan för att boka en tid.
          </p>
          <button
            onClick={onCancel}
            className="w-full text-sm text-muted-foreground underline"
          >
            Avbryt
          </button>
        </div>
      )}

      {currentStep === 'payment' && (
        <PaymentStep
          deviceId={deviceId}
          amount={policy.payment_amount || policy.metadata?.payment_amount || 0}
          paymentMethod={paymentMethod}
          onSelectMethod={setPaymentMethod}
          onPaid={handlePaid}
          onCancel={onCancel}
        />
      )}
    </div>
  );
}

function VerificationStep({
  deviceId,
  method,
  onVerified,
  onCancel,
}: {
  deviceId: string;
  method: string;
  onVerified: (token: string) => void;
  onCancel: () => void;
}) {
  switch (method) {
    case 'bankid':
      return <BankIdVerification deviceId={deviceId} onVerified={onVerified} onCancel={onCancel} />;
    case 'email':
      return <EmailVerification deviceId={deviceId} onVerified={onVerified} onCancel={onCancel} />;
    case 'mobile':
      return <SmsVerification deviceId={deviceId} onVerified={onVerified} onCancel={onCancel} />;
    default:
      return (
        <div className="text-sm text-muted-foreground">
          Verifieringsmetod &quot;{method}&quot; stöds inte ännu.
        </div>
      );
  }
}

function PaymentStep({
  deviceId,
  amount,
  paymentMethod,
  onSelectMethod,
  onPaid,
  onCancel,
}: {
  deviceId: string;
  amount: number;
  paymentMethod: 'stripe' | 'swish' | null;
  onSelectMethod: (m: 'stripe' | 'swish') => void;
  onPaid: (paymentId: string) => void;
  onCancel: () => void;
}) {
  if (!paymentMethod) {
    const amountKr = (amount / 100).toFixed(2);
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Betalning krävs</h3>
        <p className="text-sm text-muted-foreground">
          Belopp: <strong>{amountKr} kr</strong>. Välj betalningsmetod:
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onSelectMethod('swish')}
            className="flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-accent transition-colors"
          >
            <span className="text-2xl">📱</span>
            <span className="text-sm font-medium">Swish</span>
          </button>
          <button
            onClick={() => onSelectMethod('stripe')}
            className="flex flex-col items-center gap-2 p-4 border rounded-lg hover:bg-accent transition-colors"
          >
            <CreditCard className="h-6 w-6" />
            <span className="text-sm font-medium">Kort</span>
          </button>
        </div>
        <button
          onClick={onCancel}
          className="w-full text-sm text-muted-foreground underline"
        >
          Avbryt
        </button>
      </div>
    );
  }

  if (paymentMethod === 'swish') {
    return <SwishPayment deviceId={deviceId} amount={amount} onPaid={onPaid} onCancel={onCancel} />;
  }

  return <StripePayment deviceId={deviceId} amount={amount} onPaid={onPaid} onCancel={onCancel} />;
}

function getRequiredSteps(policy: AccessPolicy): Step[] {
  const steps: Step[] = [];
  if (policy.verification_required && policy.verification_required !== 'none') {
    steps.push('verification');
  }
  if (policy.booking_required === 'required') {
    steps.push('booking');
  }
  if (policy.payment_required && policy.payment_required !== 'none' && policy.payment_required !== 'postpay') {
    steps.push('payment');
  }
  return steps;
}

function getFirstStep(policy: AccessPolicy): Step {
  const steps = getRequiredSteps(policy);
  return steps[0] || 'ready';
}

function isStepComplete(step: Step, tokens: GateTokens, _policy: AccessPolicy): boolean {
  switch (step) {
    case 'verification':
      return !!tokens.verification_token;
    case 'payment':
      return !!tokens.payment_id;
    case 'booking':
      return !!tokens.booking_id;
    default:
      return true;
  }
}

function getCompletedSteps(tokens: GateTokens, policy: AccessPolicy): Step[] {
  return getRequiredSteps(policy).filter((s) => isStepComplete(s, tokens, policy));
}

function getStepLabel(step: Step): string {
  switch (step) {
    case 'verification':
      return 'Verifiering';
    case 'booking':
      return 'Bokning';
    case 'payment':
      return 'Betalning';
    default:
      return '';
  }
}
